import React, { useEffect } from 'react';
import { Form, InputNumber, Skeleton } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faPen } from '@fortawesome/free-solid-svg-icons';
import { useForm, Controller } from 'react-hook-form';
import {
  SettingsCard,
  CardHeader,
  CardTitle,
  CardTitleText,
  InfoRow,
  InfoLabel,
  InfoValue,
  FormSection,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  StyledProgress,
  ActionIconButton,
  TaxProgressWrapper,
  SectionHintText,
} from '../Styles';
import { CARD_ICON_STYLE, INFO_ICON_STYLE } from '../constants';
import { useTheme } from '../../../context/ThemeContext';
import type { TaxConfigurationSectionProps, TaxFormValues } from '../types';

const TaxConfigurationSection: React.FC<TaxConfigurationSectionProps> = ({
  user,
  editingSection,
  loading,
  profileLoading,
  onEdit,
  onSave,
  onCancel,
}) => {
  const { isDarkMode } = useTheme();
  const taxValue = user?.tax !== undefined && user?.tax !== null && !isNaN(user.tax) ? user.tax * 100 : 0;

  const { handleSubmit, control, reset } = useForm<TaxFormValues>({
    defaultValues: { tax: 0 },
  });

  useEffect(() => {
    const tax = user?.tax !== undefined && user?.tax !== null && !isNaN(user.tax) ? user.tax * 100 : 0;
    reset({ tax });
  }, [user, reset]);

  const handleCancel = () => { reset(); onCancel(); };

  return (
    <SettingsCard>
      <CardHeader>
        <CardTitle>
          <FontAwesomeIcon icon={faChartLine} style={CARD_ICON_STYLE} />
          <CardTitleText level={4}>Tax Configuration</CardTitleText>
        </CardTitle>
        <ActionIconButton onClick={profileLoading ? undefined : onEdit} iconColor="#2D6CCA" $loading={profileLoading}>
          <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
        </ActionIconButton>
      </CardHeader>

      {editingSection !== 'tax' ? (
        <div>
          <InfoRow>
            <FontAwesomeIcon icon={faChartLine} style={INFO_ICON_STYLE} />
            <InfoLabel>Tax Rate:</InfoLabel>
            <InfoValue>
              {profileLoading
                ? <Skeleton.Input active size="small" style={{ width: 100, height: 20 }} />
                : taxValue > 0 ? `${taxValue.toFixed(2)}%` : 'Not set'}
            </InfoValue>
          </InfoRow>
          <TaxProgressWrapper>
            <SectionHintText style={{ marginBottom: 8 }}>
              Current tax rate applied to all transactions
            </SectionHintText>
            <StyledProgress percent={taxValue} strokeColor="#1890ff" />
          </TaxProgressWrapper>
        </div>
      ) : (
        <Form layout="vertical" onFinish={handleSubmit(onSave)}>
          <FormSection>
            <Controller
              name="tax"
              control={control}
              rules={{
                required: 'Tax rate is required',
                min: { value: 0, message: 'Must be at least 0%' },
                max: { value: 100, message: 'Cannot exceed 100%' },
              }}
              render={({ field, fieldState }) => (
                <Form.Item
                  label="Tax Rate (%)"
                  validateStatus={fieldState.error ? 'error' : ''}
                  help={fieldState.error?.message ?? <SectionHintText>Enter the tax percentage (0–100)</SectionHintText>}
                >
                  <InputNumber
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Enter tax percentage"
                    min={0}
                    max={100}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              )}
            />
            <ActionButtons>
              <SaveChangesButton icon={<CheckOutlined />} htmlType="submit" loading={loading}>
                Save Changes
              </SaveChangesButton>
              <CancelButton onClick={handleCancel}>Cancel</CancelButton>
            </ActionButtons>
          </FormSection>
        </Form>
      )}
    </SettingsCard>
  );
};

export default React.memo(TaxConfigurationSection);
